import { InformationCircleIcon, ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Mesh3DViewer from '../components/annotation/Mesh3DViewer'

// Mapping from URL keypoint names to folder names
const KEYPOINT_FOLDER_MAPPING: Record<string, string> = {
  nose: 'nose',
  neck: 'neck',
  'right-shoulder': 'shoulderR',
  'right-elbow': 'elbowR',
  'right-wrist': 'wristR',
  'right-hand': 'handR',
  'left-shoulder': 'shoulderL',
  'left-elbow': 'elbowL',
  'left-wrist': 'wristL',
  'left-hand': 'handL',
  'right-hip': 'hipR',
  'right-knee': 'kneeR',
  'right-ankle': 'ankleR',
  'right-foot': 'footR',
  'left-hip': 'hipL',
  'left-knee': 'kneeL',
  'left-ankle': 'ankleL',
  'left-foot': 'footL',
  'right-eye': 'eyeR',
  'left-eye': 'eyeL',
  'right-ear': 'earR',
  'left-ear': 'earL',
}

// Import all images using Vite's import.meta.glob
const imageModules = import.meta.glob('/src/assets/label_consistency/**/*.png', { eager: true })

// Helper function to get images for a specific keypoint and visibility state
const getImagesForKeypoint = (keypointFolder: string, visibility: string): string[] => {
  const folderPath = visibility === 'not-visible' ? 'not visible' : visibility
  const relevantImages: string[] = []

  Object.entries(imageModules).forEach(([path, module]) => {
    if (path.includes(`/${keypointFolder}/`) && path.includes(`/${folderPath}/`)) {
      relevantImages.push((module as any).default || path)
    }
  })

  return relevantImages
}

// Keypoint descriptions for detailed view
const KEYPOINT_DESCRIPTIONS: Record<
  string,
  { title: string; description: string; tips: string[] }
> = {
  'right-ear': {
    title: 'Right Ear',
    description: 'The center of mass of the points of the ear.',
    tips: ['Not necessarily the ear canal'],
  },
  'left-ear': {
    title: 'Left Ear',
    description: 'The center of mass of the points of the ear.',
    tips: ['Not necessarily the ear canal'],
  },
  'right-eye': {
    title: 'Right Eye',
    description: 'The center of the eye.',
    tips: ['Not necessarily the pupil'],
  },
  'left-eye': {
    title: 'Left Eye',
    description: 'The center of the eye.',
    tips: ['Not necessarily the pupil'],
  },
  nose: {
    title: 'Nose',
    description: 'The tip of the nose.',
    tips: [],
  },
  neck: {
    title: 'Neck',
    description: 'The center of the neck between the torso and skull.',
    tips: ['Center it between the torso and skull', 'Pick the center of the circle of the neck'],
  },
  'right-shoulder': {
    title: 'Right Shoulder',
    description: 'The right shoulder joint.',
    tips: ['The point the arm rotates about'],
  },
  'right-elbow': {
    title: 'Right Elbow',
    description: 'The right elbow joint.',
    tips: ['The point the forearm rotates about', 'Look for the elbow crease'],
  },
  'right-wrist': {
    title: 'Right Wrist',
    description: 'The right wrist joint where the hand meets the forearm.',
    tips: ['Look for the wrist crease or visible joint'],
  },
  'right-hand': {
    title: 'Right Hand',
    description: 'The center of the mass of the points of the hand.',
    tips: ["Don't place it on the palm", 'Include the fingers in the center of mass of the points'],
  },
  'left-shoulder': {
    title: 'Left Shoulder',
    description: 'The left shoulder joint.',
    tips: ['The point the arm rotates about'],
  },
  'left-elbow': {
    title: 'Left Elbow',
    description: 'The left elbow joint.',
    tips: ['The point the forearm rotates about', 'Look for the elbow crease'],
  },
  'left-wrist': {
    title: 'Left Wrist',
    description: 'The left wrist joint where the hand meets the forearm.',
    tips: ['Look for the wrist crease or visible joint'],
  },
  'left-hand': {
    title: 'Left Hand',
    description: 'The center of the mass of the points of the hand.',
    tips: ["Don't place it on the palm", 'Include the fingers in the center of mass of the points'],
  },
  'right-hip': {
    title: 'Right Hip',
    description: 'The right hip joint.',
    tips: [
      'The point the leg rotates about',
      'Draw a line between the hip bone and the perineum and take the midpoint',
    ],
  },
  'right-knee': {
    title: 'Right Knee',
    description: 'The center of the right knee joint.',
    tips: ['Place between the knee cap and crease at the back of the knee'],
  },
  'right-ankle': {
    title: 'Right Ankle',
    description: 'The right ankle joint.',
    tips: [
      'Place between the two ankle bones on either side of the leg',
      'Look for the ankle crease',
    ],
  },
  'right-foot': {
    title: 'Right Foot',
    description: 'The center of mass of the points of the foot.',
    tips: ['Include the toes in the center of mass of the points'],
  },
  'left-hip': {
    title: 'Left Hip',
    description: 'The left hip joint.',
    tips: [
      'The point the leg rotates about',
      'Draw a line between the hip bone and the perineum and take the midpoint',
    ],
  },
  'left-knee': {
    title: 'Left Knee',
    description: 'The center of the left knee joint.',
    tips: ['Place between the knee cap and crease at the back of the knee'],
  },
  'left-ankle': {
    title: 'Left Ankle',
    description: 'The left ankle joint.',
    tips: [
      'Place between the two ankle bones on either side of the leg',
      'Look for the ankle crease',
    ],
  },
  'left-foot': {
    title: 'Left Foot',
    description: 'The center of mass of the points of the foot.',
    tips: ['Include the toes in the center of mass of the points'],
  },
}

export default function AnnotationGuidePage() {
  const { keypointName } = useParams<{ keypointName?: string }>()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<'visible' | 'inferred' | 'not-visible'>('visible')
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const keypointInfo = keypointName ? KEYPOINT_DESCRIPTIONS[keypointName] : null
  const keypointFolder = keypointName ? KEYPOINT_FOLDER_MAPPING[keypointName] : null

  // Get images for current keypoint and tab
  const currentImages = keypointFolder ? getImagesForKeypoint(keypointFolder, activeTab) : []
  if (keypointInfo) {
    // Keypoint-specific view
    return (
      <div>
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/annotation-guide')}
            className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to Annotation Guide"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{keypointInfo.title} Keypoint</h1>
            <p className="text-gray-600 mt-2">
              Detailed annotation guide for the {keypointInfo.title.toLowerCase()} keypoint
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left column: Description and tips */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 leading-relaxed mb-6">{keypointInfo.description}</p>

              {keypointInfo.tips.length > 0 && (
                <>
                  <ul className="space-y-3">
                    {keypointInfo.tips.map((tip, index) => (
                      <li key={index} className="flex items-start">
                        <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-gray-700 leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Image Gallery with Tabs */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Example Images</h2>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { key: 'visible', label: 'Visible' },
                    { key: 'inferred', label: 'Inferred' },
                    { key: 'not-visible', label: 'Not Visible' },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() =>
                        setActiveTab(tab.key as 'visible' | 'inferred' | 'not-visible')
                      }
                      className={`whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Image Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {currentImages.length > 0 ? (
                  currentImages.map((imageSrc, index) => (
                    <div
                      key={index}
                      className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedImage(imageSrc)}
                    >
                      <img
                        src={imageSrc}
                        alt={`${keypointInfo.title} - ${activeTab} example ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No images available for {activeTab} examples
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column: 3D model */}
          <div className="space-y-6">
            {/* Information box above 3D model */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Visibility Information</h2>
              <p className="text-gray-700 font-semibold leading-relaxed mb-2">Visible</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                    1
                  </span>
                  <span className="text-gray-700 leading-relaxed">
                    Diapers on hips, shirts on torsos, pants on legs, or medical equipment coverings
                    do NOT count as occulusions.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                    2
                  </span>
                  <span className="text-gray-700 leading-relaxed">
                    Blankets DO count as occlusions.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                    3
                  </span>
                  <span className="text-gray-700 leading-relaxed">
                    If there is insufficient information on the clothing (e.g. too loose), it DOES
                    count as occlusions.
                  </span>
                </li>
              </ul>
              <p className="text-gray-700 font-semibold leading-relaxed mb-2">Inferred</p>
              <div className="flex items-start mb-6">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                  1
                </span>
                <span className="text-gray-700 leading-relaxed">
                  Not directly visible, but able to infer the location (e.g. occluded by another
                  body part or blanket).
                </span>
              </div>
              <p className="text-gray-700 font-semibold leading-relaxed mb-2">Not visible</p>
              <div className="flex items-start">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                  1
                </span>
                <span className="text-gray-700 leading-relaxed">
                  Not clear where the landmark is.
                </span>
              </div>
            </div>

            {/* 3D Model */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3D Reference Model</h3>
                <div className="text-sm text-gray-600">
                  <p className="mb-1">
                    <span className="text-blue-600 font-medium">Rotate:</span> Left-click drag
                  </p>
                  <p className="mb-1">
                    <span className="text-green-600 font-medium">Pan:</span> Right-click drag (or
                    Shift + Left-click)
                  </p>
                  <p>
                    <span className="text-purple-600 font-medium">Zoom:</span> Scroll wheel
                  </p>
                </div>
              </div>

              <div className="relative" style={{ height: '600px' }}>
                <Mesh3DViewer />
              </div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] m-4">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              >
                <XMarkIcon className="w-8 h-8" />
              </button>
              <img
                src={selectedImage}
                alt="Full size image"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  // General annotation guide view
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Annotation Guide</h1>
          <p className="text-gray-600 mt-2">Reference guide for keypoint annotation positions</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h2 className="text-base font-semibold text-blue-900 mb-1">How to Use This Guide</h2>
            <p className="text-sm text-blue-700">
              Please refer to the keypoint positions on this 3D model. These represent the ideal
              annotation positions you should mark on 2D images. You can rotate and zoom the model
              to view from different angles for better understanding. Click on any keypoint or label
              for detailed annotation instructions.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">3D Reference Model</h3>
          <div className="text-sm text-gray-600">
            <p className="mb-1">
              <span className="text-blue-600 font-medium">Rotate:</span> Left-click drag
            </p>
            <p className="mb-1">
              <span className="text-green-600 font-medium">Pan:</span> Right-click drag (or Shift +
              Left-click)
            </p>
            <p className="mb-1">
              <span className="text-purple-600 font-medium">Zoom:</span> Scroll wheel
            </p>
            <p>
              <span className="text-orange-600 font-medium">Click:</span> Keypoints or labels for
              detailed guides
            </p>
          </div>
        </div>

        <div className="relative" style={{ height: '800px' }}>
          <Mesh3DViewer />
        </div>
      </div>
    </div>
  )
}
